import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Download, FileSpreadsheet, FileText, Save, Trash2, 
  GripVertical, Check, Settings 
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { API } from '@/lib/apiBase';

// Default columns definition
const ALL_COLUMNS = {
  invoice_number: "N° Facture",
  invoice_date: "Date",
  supplier_name: "Fournisseur",
  payer_company_name: "Société payeuse",
  product_name: "Produit",
  category_name: "Catégorie",
  entry_type: "Type",
  billing_cycle: "Cycle",
  company_name: "Société",
  establishment_name: "Établissement",
  sector_name: "Secteur",
  user_name: "Utilisateur",
  currency: "Devise",
  exchange_rate: "Taux de change",
  amount_ht: "Montant HT (original)",
  amount_ht_eur: "Montant HT (EUR)",
  vat_rate: "TVA %",
  amount_vat: "Montant TVA (original)",
  amount_vat_eur: "Montant TVA (EUR)",
  amount_ttc: "Montant TTC (original)",
  amount_ttc_eur: "Montant TTC (EUR)",
  line_type: "Type de ligne",
  refacturation_status: "Statut refacturation",
  refactured_date: "Date refacturation",
  refactured_by: "Refacturé par"
};

export const ExportDialog = ({ 
  open, 
  onOpenChange, 
  dataType = "entries", // entries, subscriptions, one_off, refacturation
  filters = {},
  entryIds = [], // IDs des entrées spécifiques à exporter (si vide, utilise les filtres)
  title = "Exporter"
}) => {
  const { token } = useAuth();
  const [format, setFormat] = useState('excel');
  const [selectedColumns, setSelectedColumns] = useState(Object.keys(ALL_COLUMNS));
  const [columnOrder, setColumnOrder] = useState(Object.keys(ALL_COLUMNS));
  const [loading, setLoading] = useState(false);
  
  // Templates
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  
  // Drag state
  const [draggedItem, setDraggedItem] = useState(null);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/export-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, [token]);

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open, fetchTemplates]);

  // Load template
  const loadTemplate = (templateId) => {
    if (!templateId) {
      // Reset to default
      setSelectedColumns(Object.keys(ALL_COLUMNS));
      setColumnOrder(Object.keys(ALL_COLUMNS));
      setFormat('excel');
      setSelectedTemplate('');
      return;
    }
    
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedColumns(template.columns || Object.keys(ALL_COLUMNS));
      setColumnOrder(template.column_order || template.columns || Object.keys(ALL_COLUMNS));
      setFormat(template.format || 'excel');
      setSelectedTemplate(templateId);
    }
  };

  // Save template
  const saveTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error('Le nom du modèle est requis');
      return;
    }

    try {
      await axios.post(`${API}/export-templates`, {
        name: newTemplateName,
        description: newTemplateDescription,
        format: format,
        columns: selectedColumns,
        column_order: columnOrder
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Modèle enregistré');
      setSaveDialogOpen(false);
      setNewTemplateName('');
      setNewTemplateDescription('');
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  // Delete template
  const deleteTemplate = async () => {
    if (!templateToDelete) return;
    
    try {
      await axios.delete(`${API}/export-templates/${templateToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Modèle supprimé');
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      if (selectedTemplate === templateToDelete.id) {
        loadTemplate('');
      }
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Toggle column selection
  const toggleColumn = (columnKey) => {
    setSelectedColumns(prev => {
      if (prev.includes(columnKey)) {
        return prev.filter(c => c !== columnKey);
      } else {
        return [...prev, columnKey];
      }
    });
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    
    const newOrder = [...columnOrder];
    const item = newOrder[draggedItem];
    newOrder.splice(draggedItem, 1);
    newOrder.splice(index, 0, item);
    setColumnOrder(newOrder);
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  // Generate export
  const handleExport = async () => {
    if (selectedColumns.length === 0) {
      toast.error('Sélectionnez au moins une colonne');
      return;
    }

    setLoading(true);
    try {
      // Order selected columns according to columnOrder
      const orderedColumns = columnOrder.filter(col => selectedColumns.includes(col));
      
      const response = await axios.post(`${API}/export/generate`, {
        data_type: dataType,
        format: format,
        columns: orderedColumns,
        filters: filters,
        entry_ids: entryIds.length > 0 ? entryIds : undefined
      }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Download file
      const blob = new Blob([response.data], { 
        type: format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export_${new Date().toISOString().slice(0,10)}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Export téléchargé');
      onOpenChange(false);
    } catch (error) {
      console.error('Error generating export:', error);
      toast.error('Erreur lors de la génération de l\'export');
    } finally {
      setLoading(false);
    }
  };

  // Get ordered columns for display
  const orderedColumnsForDisplay = columnOrder.filter(col => col in ALL_COLUMNS);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-card max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              {title}
            </DialogTitle>
            <DialogDescription>
              Personnalisez votre export et téléchargez-le
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="export" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="export">Export</TabsTrigger>
              <TabsTrigger value="templates">
                <Settings className="w-4 h-4 mr-2" />
                Modèles
              </TabsTrigger>
            </TabsList>

            <TabsContent value="export" className="space-y-4 pt-4">
              {/* Format selection */}
              <div className="space-y-2">
                <Label>Format</Label>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant={format === 'excel' ? 'default' : 'outline'}
                    onClick={() => setFormat('excel')}
                    className="flex-1"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Excel
                  </Button>
                  <Button
                    type="button"
                    variant={format === 'pdf' ? 'default' : 'outline'}
                    onClick={() => setFormat('pdf')}
                    className="flex-1"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </div>

              {/* Template selection */}
              <div className="space-y-2">
                <Label>Modèle enregistré</Label>
                <Select 
                  value={selectedTemplate || "_default"} 
                  onValueChange={(val) => loadTemplate(val === "_default" ? "" : val)}
                >
                  <SelectTrigger data-testid="template-select-trigger">
                    <SelectValue placeholder="Sélectionner un modèle ou personnaliser..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_default" data-testid="template-option-default">
                      Personnalisé (tous les champs)
                    </SelectItem>
                    {templates.map(template => (
                      <SelectItem key={template.id} value={template.id} data-testid={`template-option-${template.id}`}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Column selection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Colonnes à exporter ({selectedColumns.length} sélectionnées)</Label>
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setSelectedColumns(Object.keys(ALL_COLUMNS))}
                    >
                      Tout
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setSelectedColumns([])}
                    >
                      Aucun
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 p-3 border border-border rounded-lg max-h-48 overflow-y-auto">
                  {Object.entries(ALL_COLUMNS).map(([key, label]) => (
                    <div key={key} className="flex items-center space-x-2">
                      <Checkbox
                        id={`col-${key}`}
                        checked={selectedColumns.includes(key)}
                        onCheckedChange={() => toggleColumn(key)}
                      />
                      <label
                        htmlFor={`col-${key}`}
                        className="text-sm cursor-pointer"
                      >
                        {label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Column order */}
              <div className="space-y-2">
                <Label>Ordre des colonnes (glisser-déposer)</Label>
                <div className="border border-border rounded-lg max-h-48 overflow-y-auto">
                  {orderedColumnsForDisplay
                    .filter(col => selectedColumns.includes(col))
                    .map((col, index) => (
                      <div
                        key={col}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`flex items-center gap-2 px-3 py-2 cursor-move hover:bg-muted/50 ${
                          draggedItem === index ? 'bg-primary/10' : ''
                        } ${index > 0 ? 'border-t border-border' : ''}`}
                      >
                        <GripVertical className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{ALL_COLUMNS[col]}</span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Save as template button */}
              <Button
                variant="outline"
                onClick={() => setSaveDialogOpen(true)}
                className="w-full"
              >
                <Save className="w-4 h-4 mr-2" />
                Enregistrer comme modèle
              </Button>
            </TabsContent>

            <TabsContent value="templates" className="space-y-4 pt-4">
              {templates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Settings className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucun modèle enregistré</p>
                  <p className="text-sm">Créez votre premier modèle depuis l'onglet Export</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {templates.map(template => (
                    <div 
                      key={template.id}
                      className="flex items-center justify-between p-3 border border-border rounded-lg"
                    >
                      <div>
                        <p className="font-medium">{template.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {template.format === 'excel' ? 'Excel' : 'PDF'} • {template.columns?.length || 0} colonnes
                        </p>
                        {template.description && (
                          <p className="text-xs text-muted-foreground mt-1">{template.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            loadTemplate(template.id);
                          }}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Utiliser
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setTemplateToDelete(template);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button onClick={handleExport} disabled={loading || selectedColumns.length === 0}>
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Exporter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>Enregistrer le modèle</DialogTitle>
            <DialogDescription>
              Donnez un nom à votre modèle d'export
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom du modèle *</Label>
              <Input
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="Ex: Export comptable"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optionnel)</Label>
              <Input
                value={newTemplateDescription}
                onChange={(e) => setNewTemplateDescription(e.target.value)}
                placeholder="Description du modèle..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={saveTemplate}>
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Template Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le modèle ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le modèle "{templateToDelete?.name}" sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={deleteTemplate} className="bg-red-500 hover:bg-red-600">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
